import React, { useCallback, useEffect, useState } from 'react'
import {
  Table,
  Pagination,
  Tooltip,
  CircularProgress,
  LoadingModal,
  Icon
} from 'components'
import { useDispatch, useSelector } from 'store/store'
import { toast } from 'react-toastify'
import {
  asyncGetNegotiations,
  setSortFilter,
  setCurrentPage,
  setNegotiationsList
} from 'store/NegotiationsContext/negotiationsSlice'
import { FormProvider, useForm } from 'react-hook-form'
import { setJustificationVeredict, downloadPlanilhaND } from 'services/nd'
import {
  JustificationVeredict,
  ResponseJustificationVeredict,
  NDPayload
} from 'services/nd/interface'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'

import ModalConfirmation from '../ModalConfirmation'

import * as S from './styles'
import ModalNDDetails from '../ModalNDDetails'
import { Negotiation } from 'store/NegotiationsContext/Types/NegotiationType'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { ActionPerm } from 'components/Structures/Header/constants'
import { usePermissions } from 'commons/customHooks/useAuth'
import headColumns from './headColumns'
import { theme } from 'styles/theme/hav-theme'
import { CustomBoolean } from 'constants/enums'
import { formatDate } from 'commons/utils/date'
import { YesNo } from 'constants/enums'

type NDTableProps = {
  payload: NDPayload
}

export enum OPERATION_STATE {
  APPROVE = 'A',
  REJECT = 'R'
}

const defaultDifferentiatedNegotiation = {
  Anexo: false,
  AprovacaoBackoffice: false,
  Aprovador: '',
  Causa: '',
  CodigoEquipeVenda: '',
  CodigoRegiaoVenda: '',
  CodigoSapCliente: '',
  CondicaoPagamento: '',
  DataAlteracao: '',
  DataAprovacao: '',
  DataSolicitacao: '',
  EquipeVenda: '',
  ForaDeLinha: false,
  FlagDescontoDiferente: 'N' as CustomBoolean,
  MesCota: '',
  NumeroOrdemVendaPortal: '',
  PrazoAplicado: 0,
  PrazoDevido: 0,
  QuantidadePares: 0,
  PercentualDescontoAplicado: 0,
  PercentualDescontoExcedido: 0,
  PercentualDescontoMaximo: 0,
  RazaoSocial: '',
  RegiaoVenda: '',
  RowsCount: 0,
  TipoOrdemVenda: '',
  ValorDescontoAplicado: 0,
  ValorDescontoMaximo: 0,
  ValorDescontoExcedente: 0,
  ValorPrecoMedio: 0,
  checked: false,
  SolicitaAprovacaoAlcada: false,
  UsuarioDiretor: false
}

// TODO: Refactor to be more readable and performant
const NDTable = ({ payload }: NDTableProps) => {
  const dispatch = useDispatch()
  const [loadingReport, setLoadingReport] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(false)
  const [operation, setOperation] = useState<OPERATION_STATE>(OPERATION_STATE.APPROVE)
  const { verifyPermission } = usePermissions()

  const [multipleOvs, setMultipleOvs] = useState(false)

  const [hasAuthority, setHasAuthority] = useState(true)
  const [isDirector, setIsDirector] = useState(false)
  const [ov, setOv] = useState<Negotiation>(defaultDifferentiatedNegotiation)
  const [loading, setLoading] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [shouldShowDetails, setShouldShowDetails] = useState(false)
  const { negotiationsList, sortFilter, pagination } = useSelector(
    (store) => store.negotiations
  )

  const methods = useForm({
    defaultValues: { justification: '' }
  })

  const { reset, watch } = methods

  useEffect(() => {
    if (confirmationModal) {
      reset()
    }
  }, [confirmationModal])

  const shouldDisable = useCallback(
    (action: ActionPerm) => !verifyPermission(SCREENS.ND_MANAGE, action),
    [verifyPermission]
  )

  const renderCause = (cause: string) => {
    if (!cause) return '-'
    if (cause === 'Prazo e Desconto') return 'Desc. + Prazo'
    return cause
  }

  const renderActions = (item: Negotiation) => {
    return (
      <>
        {item.AprovacaoBackoffice && !item.UsuarioDiretor ? (
          <Tooltip title="Aguardando aprovação da diretoria" placement="top" arrow>
            <S.WrapClockIcon>
              <Icon icon="clock" color={theme.colors.primary.main} size="15" />
            </S.WrapClockIcon>
          </Tooltip>
        ) : (
          <S.ActionsCell key="actions1">
            <Tooltip
              title={shouldDisable(ACTIONS_SCREENS.APPROVE) ? '' : 'Aprovar Negociação'}
              placement="top"
              arrow
            >
              <S.ApproveButton
                disabled={shouldDisable(ACTIONS_SCREENS.APPROVE)}
                onClick={() => handleConfirmation(OPERATION_STATE.APPROVE, item)}
              >
                <Icon icon="task-alt" color={theme.colors.neutral.white} size="15" />
              </S.ApproveButton>
            </Tooltip>

            <Tooltip
              title={shouldDisable(ACTIONS_SCREENS.DISMISS) ? '' : 'Reprovar Negociação'}
              placement="top"
              arrow
            >
              <S.RejectButton
                disabled={shouldDisable(ACTIONS_SCREENS.DISMISS)}
                onClick={() => handleConfirmation(OPERATION_STATE.REJECT, item)}
              >
                <Icon icon="cancel" color={theme.colors.neutral.white} size="15" />
              </S.RejectButton>
            </Tooltip>
          </S.ActionsCell>
        )}
      </>
    )
  }

  const renderTableBody = () => {
    return negotiationsList.map((item) => {
      return {
        id: `${item.NumeroOrdemVendaPortal}`,
        checked: item.checked ?? false,
        onDoubleClick: () => {
          setOv(item)
          setShowDetails(true)
          setShouldShowDetails(true)
        },

        content: [
          <S.IconWrapper key="tbody-1">
            <Icon
              icon="file"
              size="15"
              color={
                item.Anexo ? theme.colors.primary.main : theme.colors.neutral.mediumhover
              }
            />
          </S.IconWrapper>,
          renderCause(item.Causa),
          `${formatDate(item.DataSolicitacao)}`,
          item.CodigoSapCliente,
          item.RazaoSocial,
          item.NumeroOrdemVendaPortal,
          item.CondicaoPagamento,
          `${item.PrazoDevido} dias`,
          item.PrazoAplicado > item.PrazoDevido ? (
            <S.WarningText>{item.PrazoAplicado} dias</S.WarningText>
          ) : (
            `${item.PrazoAplicado} dias`
          ),
          <React.Fragment key="tbody-8">
            <strong>{item.PercentualDescontoMaximo}%</strong> •{' '}
            {formatToPTBRNumber(item.ValorDescontoMaximo)}
          </React.Fragment>,
          item.ValorDescontoAplicado > item.ValorDescontoMaximo ? (
            <S.WarningText>
              <strong>{item.PercentualDescontoAplicado}%</strong> •{' '}
              {formatToPTBRNumber(item.ValorDescontoAplicado)}
            </S.WarningText>
          ) : (
            <>
              <strong>{item.PercentualDescontoAplicado}%</strong> •{' '}
              {formatToPTBRNumber(item.ValorDescontoAplicado)}
            </>
          ),
          <S.WrapIcon key="tbody-10">
            <span>
              <strong>{item.PercentualDescontoExcedido}%</strong> •{' '}
              {formatToPTBRNumber(item.ValorDescontoExcedente)}
            </span>
            {item.FlagDescontoDiferente === YesNo.YES && (
              <Tooltip
                title="Há materiais com descontos diferentes"
                placement="top"
                arrow
              >
                <Icon icon="report" size="15" color={theme.colors.alert.warning.dark} />
              </Tooltip>
            )}
          </S.WrapIcon>,
          item.Aprovador ? item.Aprovador : '',
          formatDate(item.DataAprovacao),
          numberToDecimalString(item.QuantidadePares),
          item.TipoOrdemVenda,
          item.ForaDeLinha ? 'Sim' : 'Não',
          formatDate(item.MesCota, 'MM/YYYY', 'YYYYMM'),
          renderActions(item)
        ]
      }
    })
  }

  const setCheckedND = (checked: boolean, id?: string) => {
    if (id) {
      const newNegotiationsList = negotiationsList.map((item) => {
        if (item.NumeroOrdemVendaPortal === id) {
          return { ...item, checked }
        }
        return item
      })
      dispatch(setNegotiationsList(newNegotiationsList))
    } else {
      const newNegotiationsList = negotiationsList.map((item) => {
        return { ...item, checked }
      })
      dispatch(setNegotiationsList(newNegotiationsList))
    }
  }

  const getCheckedND = () => {
    return negotiationsList.filter((item) => item.checked)
  }

  const getCheckedNDsList = () => {
    const negotiations = getCheckedND()
    const ovs = negotiations.map((item) => {
      return {
        NumeroOrdemVendaPortal: item.NumeroOrdemVendaPortal,
        DataAlteracao: item.DataAlteracao,
        hasAuthority: item.SolicitaAprovacaoAlcada,
        UsuarioDiretor: item.UsuarioDiretor,
        AprovacaoBackoffice: item.AprovacaoBackoffice
      }
    })

    return ovs.filter(
      (elem, index) =>
        ovs.findIndex(
          (obj) => obj.NumeroOrdemVendaPortal === elem.NumeroOrdemVendaPortal
        ) === index
    )
  }

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page))
  }
  const justification = watch('justification')

  const handleConfirmation = (op: OPERATION_STATE, ovNumber?: Negotiation) => {
    setOperation(op)
    setConfirmationModal(true)
    if (shouldShowDetails) {
      setShouldShowDetails(false)
    }
    if (ovNumber) {
      setOv(ovNumber)
      setHasAuthority(ovNumber.SolicitaAprovacaoAlcada)
      setIsDirector(ovNumber.UsuarioDiretor)
    } else {
      const checkedsNDs = getCheckedNDsList()
      const hasMultipleOvs = checkedsNDs.length > 1
      setMultipleOvs(hasMultipleOvs)

      if (checkedsNDs.length > 0) {
        const hasSomeAuthority = checkedsNDs.some((item) => item.hasAuthority)
        const isUserDirector = checkedsNDs.some((item) => item.UsuarioDiretor)

        setHasAuthority(hasSomeAuthority)
        setIsDirector(isUserDirector)
      }
    }
  }

  const willDeletePage = (callback: (item: Negotiation) => boolean) => {
    return operation === OPERATION_STATE.REJECT && negotiationsList.every(callback)
  }

  const handleOperation = async () => {
    setLoading(true)

    if (ov.NumeroOrdemVendaPortal !== '') {
      await setJustificationVeredict([
        {
          numeroOrdemVenda: ov.NumeroOrdemVendaPortal,
          motivoReprovacao: justification,
          flagOperacaoFluxoAprovacao: operation,
          dataAlteracao: ov.DataAlteracao
        }
      ])
        .then((res) => {
          if (res[0].OperacaoRealizada) {
            if (hasAuthority && !isDirector && operation === OPERATION_STATE.APPROVE) {
              toast.warn(
                `${'A sua aprovação foi enviada com sucesso. No entanto, sendo uma negociação fora da sua alçada, requer a aprovação da diretoria para prosseguir.'}`
              )
            } else {
              toast.success(
                `Negociação ${
                  operation === OPERATION_STATE.APPROVE ? 'aprovada' : 'reprovada' // TODO: Extract to function
                } com sucesso!`
              )
            }

            dispatch(
              asyncGetNegotiations(
                payload,
                willDeletePage(
                  (item) => item.NumeroOrdemVendaPortal === ov.NumeroOrdemVendaPortal
                ) && pagination.currentPage > 1
                  ? pagination.currentPage - 1
                  : pagination.currentPage,
                sortFilter
              )
            )
          }
          // TODO: Review if this ELSE is necessary
          else {
            toast.error(
              `Não foi possível ${
                operation === OPERATION_STATE.APPROVE ? 'aprovar' : 'reprovar'
              } a negociação. Tente novamente mais tarde!`
            )
          }
          setOv(defaultDifferentiatedNegotiation)
        })
        .catch((error) => {
          toast.error(error.response.data[0].Mensagem)
          setOv(defaultDifferentiatedNegotiation)
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      const ovs: JustificationVeredict = []
      const checkedsNds = getCheckedNDsList()
      checkedsNds.forEach((item) => {
        ovs.push({
          numeroOrdemVenda: item.NumeroOrdemVendaPortal,
          motivoReprovacao: justification,
          flagOperacaoFluxoAprovacao: operation,
          dataAlteracao: item.DataAlteracao
        })
      })

      await setJustificationVeredict(ovs)
        .then((res: ResponseJustificationVeredict) => {
          const successful = res.filter((item) => item.OperacaoRealizada).length
          const errors = res
            .filter((item) => !item.OperacaoRealizada)
            .map((ovs) => {
              return ovs.NumeroOrdemVenda
            })
          const total = res.length

          if (successful) {
            if (operation === OPERATION_STATE.APPROVE) {
              if (checkedsNds.some((item) => item.hasAuthority && !item.UsuarioDiretor)) {
                toast.warn(
                  `${'As negociações fora da sua alçada foram enviadas para a aprovação da diretoria.'}`
                )
              }

              // TODO: Extract to function
              if (checkedsNds.some((item) => !item.hasAuthority || item.UsuarioDiretor)) {
                successful > 1 && toast.success(`${'Negociações aprovadas com sucesso!'}`)
                successful === 1 && toast.success(`${'Negociação aprovada com sucesso!'}`)
              }
            } else {
              // TODO: Extract to function
              successful > 1 && toast.success(`${'Negociações reprovadas com sucesso!'}`)
              successful === 1 && toast.success(`${'Negociação reprovada com sucesso!'}`)
            }

            errors.length > 0 &&
              toast.error(
                `Não foi possível ${
                  operation === OPERATION_STATE.APPROVE ? 'aprovar' : 'reprovar'
                }as seguintes OVs: \n ${errors.join(', ')}`
              )

            dispatch(
              asyncGetNegotiations(
                payload,
                willDeletePage((item) => Boolean(item?.checked)) &&
                  pagination.currentPage > 1
                  ? pagination.currentPage - 1
                  : pagination.currentPage,
                sortFilter
              )
            )
          } else {
            toast.error(
              `Não foi possível ${
                operation === OPERATION_STATE.APPROVE ? 'aprovar ' : 'reprovar '
              } ${total - successful} de ${total} ${
                total > 1 ? 'negociações.' : 'negociação.'
              } Tente novamente mais tarde!`
            )
          }

          dispatch(
            asyncGetNegotiations(
              payload,
              willDeletePage((item) => Boolean(item?.checked)) &&
                pagination.currentPage > 1
                ? pagination.currentPage - 1
                : pagination.currentPage,
              sortFilter
            )
          )
          setOv(defaultDifferentiatedNegotiation)
        })
        .catch(() => {
          toast.error(
            `Não foi possível ${
              operation === OPERATION_STATE.APPROVE ? 'aprovar' : 'reprovar'
            } a negociação. Tente novamente mais tarde!`
          )
          setOv(defaultDifferentiatedNegotiation)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }
  const NDTableFooter = () => {
    return (
      <S.FooterDiv>
        <S.FooterInfo>
          Os valores destacados indicam os critérios que tornaram a ordem de venda uma
          negociação diferenciada.
        </S.FooterInfo>
        <S.FooterRow>
          <Pagination
            numberOfPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            setCurrentPage={handlePageChange}
            numberOfItems={pagination.totalItems}
            itemsPerPage={pagination.pageSize}
          />
          <S.FooterActions>
            <S.FooterButton
              disabled={shouldDisable(ACTIONS_SCREENS.DOWNLOAD) || loadingReport}
              onClick={getReport}
            >
              {loadingReport ? <CircularProgress size="24px" /> : 'Baixar relatório'}
            </S.FooterButton>

            {!!getCheckedND().length &&
              !getCheckedNDsList().some(
                (item) => item.AprovacaoBackoffice && !item.UsuarioDiretor
              ) && (
                <S.ApprovalActions>
                  <S.FooterButton
                    disabled={shouldDisable(ACTIONS_SCREENS.DISMISS)}
                    onClick={() => {
                      handleConfirmation(OPERATION_STATE.REJECT)
                    }}
                  >
                    Reprovar
                  </S.FooterButton>
                  <S.FooterButton
                    fill
                    disabled={shouldDisable(ACTIONS_SCREENS.APPROVE)}
                    onClick={() => handleConfirmation(OPERATION_STATE.APPROVE)}
                  >
                    Aprovar
                  </S.FooterButton>
                </S.ApprovalActions>
              )}
          </S.FooterActions>
        </S.FooterRow>
      </S.FooterDiv>
    )
  }

  const getReport = async () => {
    try {
      setLoadingReport(true)
      const response = await downloadPlanilhaND(payload)

      // TODO: Replace with utils/downlodFile({file, fileName, fileType})
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'negociacoes_diferenciadas.xlsx')
      link.click()
      toast.success('Seu download iniciará em instantes!')
    } catch (error) {
      toast.error('Não foi possível baixar o relatório. Tente novamente mais tarde.')
    } finally {
      setLoadingReport(false)
    }
  }
  const handleContinue = () => {
    handleOperation()
    setConfirmationModal(!confirmationModal)
  }

  const modalConfirmationSetIsOpen = () => {
    setConfirmationModal(!confirmationModal)

    if (!shouldShowDetails) {
      setShouldShowDetails(true)
    }
  }

  return (
    <>
      {loading ? <LoadingModal /> : null}
      <S.TableWrapper>
        {renderTableBody().length > 0 ? (
          <Table
            headData={headColumns}
            bodyData={renderTableBody()}
            setChecked={setCheckedND}
            hasCheckbox
            rowHeight={38}
            iconColumns={[0]}
            actionColumns={[18]}
            sortValue={sortFilter}
            sortHandle={({ column, isCrescent }) => {
              dispatch(setSortFilter({ column, isCrescent }))
            }}
          />
        ) : (
          <Table headData={[]} bodyData={[]} />
        )}

        <FormProvider {...methods}>
          <ModalConfirmation
            isOpen={confirmationModal}
            isReject={operation === OPERATION_STATE.REJECT}
            isMultiple={multipleOvs}
            setIsOpen={() => modalConfirmationSetIsOpen()}
            hasAuthority={hasAuthority && !isDirector}
            handleContinue={() => {
              handleContinue()
            }}
            handleReturnAction={() => modalConfirmationSetIsOpen()}
          />
          {showDetails && ov && (
            <ModalNDDetails
              showCheck={ov?.AprovacaoBackoffice}
              handleConfirmation={handleConfirmation}
              isOpen={showDetails && shouldShowDetails}
              setIsOpen={setShowDetails}
              ovNumber={ov?.NumeroOrdemVendaPortal}
              ovType={ov?.TipoOrdemVenda}
              isDirector={ov?.UsuarioDiretor}
              negotiation={ov}
            />
          )}
        </FormProvider>
        {renderTableBody().length > 0 && <NDTableFooter />}
      </S.TableWrapper>
    </>
  )
}

export default NDTable
